import { create } from 'zustand';
import type { Car } from '@/types';

const MAX_COMPARE_CARS = 4;

interface CompareState {
  compareCars: Car[];
  isBarExpanded: boolean;

  // Actions
  addToCompare: (car: Car) => boolean;
  removeFromCompare: (carId: string) => void;
  clearCompare: () => void;
  isInCompare: (carId: string) => boolean;
  toggleCompare: (car: Car) => boolean;
  setBarExpanded: (expanded: boolean) => void;
  toggleBarExpanded: () => void;
  canAddMore: () => boolean;
}

export const useCompareStore = create<CompareState>((set, get) => ({
  compareCars: [],
  isBarExpanded: false,

  addToCompare: (car: Car) => {
    const { compareCars } = get();

    // Check if already in compare
    if (compareCars.some((c) => c.id === car.id)) {
      return false;
    }

    // Check max limit
    if (compareCars.length >= MAX_COMPARE_CARS) {
      return false;
    }

    set({
      compareCars: [...compareCars, car],
      // Keep bar collapsed - user can expand if they want
    });

    return true;
  },

  removeFromCompare: (carId: string) => {
    const { compareCars } = get();
    set({
      compareCars: compareCars.filter((car) => car.id !== carId),
    });
  },

  clearCompare: () => {
    set({
      compareCars: [],
      isBarExpanded: false,
    });
  },

  isInCompare: (carId: string) => {
    return get().compareCars.some((car) => car.id === carId);
  },

  toggleCompare: (car: Car) => {
    const { compareCars, addToCompare, removeFromCompare } = get();

    if (compareCars.some((c) => c.id === car.id)) {
      removeFromCompare(car.id);
      return false;
    } else {
      return addToCompare(car);
    }
  },

  setBarExpanded: (expanded: boolean) => {
    set({ isBarExpanded: expanded });
  },

  toggleBarExpanded: () => {
    set((state) => ({ isBarExpanded: !state.isBarExpanded }));
  },

  canAddMore: () => {
    return get().compareCars.length < MAX_COMPARE_CARS;
  },
}));
